import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Globe, Users, Award, Headphones, MapPin, Calendar, ChevronRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PackageCard from "../components/PackageCard";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMAGE = "https://images.unsplash.com/photo-1743356174397-d6da6f014f8f?w=1920&q=80";

const HomePage = () => {
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/packages?featured=true&audience=website`);
      setPackages(response.data);
    } catch (error) {
      console.error("Error fetching packages:", error);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Globe, title: "Global Destinations", desc: "100+ destinations worldwide" },
    { icon: Users, title: "B2B Partners", desc: "500+ trusted travel agents" },
    { icon: Award, title: "Best Prices", desc: "Competitive agent rates" },
    { icon: Headphones, title: "24/7 Support", desc: "Round the clock assistance" },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={HERO_IMAGE} 
            alt="Luxury Travel" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/80 via-primary-900/50 to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <span className="inline-block text-secondary-500 font-accent text-2xl mb-4">
              Your Trusted B2B Partner
            </span>
            <h1 className="font-heading text-5xl md:text-7xl text-white leading-tight mb-6">
              Craft <span className="text-secondary-500">Unforgettable</span> Journeys
            </h1>
            <p className="text-white/80 text-lg md:text-xl leading-relaxed mb-8">
              Partner with Happy Holidays DMC for premium travel packages, competitive rates, and seamless booking experiences for your clients.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/packages">
                <Button size="lg" className="bg-secondary-500 hover:bg-secondary-700 text-white gap-2" data-testid="explore-packages-btn">
                  Explore Packages <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/agent-register">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" data-testid="become-agent-btn">
                  Become an Agent
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-6 rounded-lg hover:bg-primary-50 transition-colors"
              >
                <feature.icon className="w-10 h-10 text-primary-700 mx-auto mb-4" />
                <h3 className="font-heading text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Packages */}
      <section className="py-20 md:py-32 bg-background">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-secondary-500 font-accent text-xl">Handpicked for You</span>
            <h2 className="font-heading text-4xl md:text-5xl mt-2 mb-4">Featured Packages</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Discover our most popular travel packages, carefully curated for unforgettable experiences.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-700" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {packages.slice(0, 3).map((pkg, index) => (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <PackageCard pkg={pkg} onViewDetails={setSelectedPackage} />
                </motion.div>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/packages">
              <Button variant="outline" size="lg" className="gap-2" data-testid="view-all-packages-btn">
                View All Packages <ChevronRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-700">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
            Ready to Partner with Us?
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto mb-8">
            Join 500+ travel agents who trust Happy Holidays DMC for competitive rates, fast quotes, and exceptional service.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/agent-register">
              <Button size="lg" className="bg-secondary-500 hover:bg-secondary-700 text-white" data-testid="cta-register-btn">
                Register as Agent
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" data-testid="cta-contact-btn">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {/* Package Detail Dialog */}
      <Dialog open={!!selectedPackage} onOpenChange={() => setSelectedPackage(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedPackage && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">{selectedPackage.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <img 
                  src={selectedPackage.image_url} 
                  alt={selectedPackage.title}
                  className="w-full h-48 object-cover rounded-lg"
                />
                <div className="flex gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {selectedPackage.destination}</span>
                  <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {selectedPackage.duration}</span>
                </div>
                <p className="text-foreground">{selectedPackage.description}</p>
                
                <div>
                  <h4 className="font-semibold mb-2">Highlights</h4>
                  <ul className="grid grid-cols-2 gap-2">
                    {selectedPackage.highlights?.map((h, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <span className="w-1.5 h-1.5 bg-primary-700 rounded-full" /> {h}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Inclusions</h4>
                  <ul className="space-y-1">
                    {selectedPackage.inclusions?.map((inc, i) => (
                      <li key={i} className="text-sm text-muted-foreground">✓ {inc}</li>
                    ))}
                  </ul>
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <div>
                    <span className="text-sm text-muted-foreground">Starting from</span>
                    <p className="text-2xl font-bold text-primary-700">₹{selectedPackage.price?.toLocaleString()}</p>
                  </div>
                  <Link to="/agent-register">
                    <Button className="bg-primary-700 hover:bg-primary-900">
                      Request Quote
                    </Button>
                  </Link>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomePage;
